import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ExternalLink, Phone, Search, FileUp, MessageSquare,
  Plane, Stethoscope, Clock, ShieldCheck, Bot, Sparkles, ListChecks,
} from "lucide-react";
import WorkforceOsLayout from "../../../components/workforce_os/WorkforceOsLayout";
import RebecaMiguelChatbot from "../../../components/workforce_os/RebecaMiguelChatbot";
import { SUBMIT_RESUME_URL, RECRUITER_SMS_TEL, RECRUITER_PHONE_HUMAN } from "../../../data/recruitingLinks";

/* ------------------------------------------------------------------ */
/*  /workforce-os/ai-nurse-recruiter                                  */
/*  Dedicated landing for Rebeca Miguel™ — embedded chat + content    */
/* ------------------------------------------------------------------ */

const CAPABILITIES = [
  "Explore roles by specialty + location",
  "Understand travel / local / per diem / permanent options",
  "Prepare for recruiter follow-up",
  "Walk through the next step on tritalcare.com",
  "Get connected to a human TRITAL Care recruiter",
];

const HOW_IT_WORKS = [
  {
    step: "01",
    title: "Tell Rebeca your specialty",
    body: "She asks one question at a time — specialty, location, job type, shift, license state, and availability.",
    icon: MessageSquare,
  },
  {
    step: "02",
    title: "Get pointed to the right next step",
    body: "Live jobs, resume submission, recruiter text — Rebeca surfaces the exact link you need without inventing job listings.",
    icon: ListChecks,
  },
  {
    step: "03",
    title: "Hand off to a human recruiter",
    body: "When you're ready, Rebeca captures your details and routes them to a TRITAL Care recruiter for follow-up.",
    icon: Sparkles,
  },
];

const AUDIENCES = [
  {
    icon: Plane,
    eyebrow: "For Travel Nurses",
    title: "13-week contracts, structured & seasonal",
    bullets: [
      "ICU, ER, Med-Surg, OR, OB, PCU, Tele, NICU and more",
      "License-state guidance for compact + non-compact states",
      "Talk through start-date windows and shift preferences",
    ],
  },
  {
    icon: Stethoscope,
    eyebrow: "For Allied Health",
    title: "PT, OT, SLP, RT, Imaging, Lab",
    bullets: [
      "Specialty-specific recruiting workflow",
      "Submission packets and compliance walkthrough",
      "Modality + setting matching (inpatient, SNF, outpatient, school)",
    ],
  },
  {
    icon: Clock,
    eyebrow: "For PRN, Local & Permanent",
    title: "Flexible shifts, local contracts, perm placement",
    bullets: [
      "Per-diem / PRN availability and shift preferences",
      "Local-to-home contracts and W-2 placement options",
      "Permanent / direct-hire role guidance with TRITAL Care",
    ],
  },
];

export default function AiNurseRecruiter() {
  React.useEffect(() => {
    document.title = "AI Nurse Recruiter — Rebeca Miguel™ | TRITAL Workforce OS™";
  }, []);

  return (
    <WorkforceOsLayout
      breadcrumb="AI Nurse Recruiter"
      title="Meet Rebeca Miguel™ — AI Nurse Recruiter"
      description="A conversational AI recruiting assistant that helps clinicians explore jobs, submit resumes, complete checklists, and connect with TRITAL Care. Available 24/7. Final review by a real TRITAL Care recruiter."
      showAiDisclaimer={true}
    >
      {/* HERO CTAs ----------------------------------------------------- */}
      <section className="bg-white" data-testid="wos-ai-hero-ctas">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8 -mt-3 pb-8">
          <div className="flex flex-wrap gap-2.5">
            <a
              href="#rebeca-chat"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-trital-navy text-white text-[13.5px] font-semibold hover:bg-trital-navy-deep"
              data-testid="wos-ai-hero-chat"
            >
              <Bot className="w-4 h-4" strokeWidth={1.8} /> Chat with Rebeca
            </a>
            <Link
              to="/careers/jobs"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-trital-red text-white text-[13.5px] font-semibold hover:bg-trital-red-deep"
              data-testid="wos-ai-hero-jobs"
            >
              <Search className="w-4 h-4" strokeWidth={1.8} /> Search Jobs
            </Link>
            <a
              href={SUBMIT_RESUME_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-slate-300 text-trital-navy text-[13.5px] font-semibold hover:bg-slate-50"
              data-testid="wos-ai-hero-resume"
            >
              <FileUp className="w-4 h-4" strokeWidth={1.8} /> Submit Resume <ExternalLink className="w-3.5 h-3.5 opacity-70" />
            </a>
            <a
              href={`sms:${RECRUITER_SMS_TEL}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-slate-300 text-trital-navy text-[13.5px] font-semibold hover:bg-slate-50"
              data-testid="wos-ai-hero-text"
            >
              <Phone className="w-4 h-4" strokeWidth={1.8} /> Text Recruiter
              <span className="hidden sm:inline tabular-nums text-[12px] text-slate-500 ml-1">{RECRUITER_PHONE_HUMAN}</span>
            </a>
          </div>
        </div>
      </section>

      {/* CHAT + ASIDE -------------------------------------------------- */}
      <section id="rebeca-chat" className="py-8 scroll-mt-24" data-testid="wos-ai-page">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-10">
          <div data-testid="wos-ai-chat-wrap">
            <RebecaMiguelChatbot variant="embedded" />
          </div>

          <aside className="space-y-5">
            <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="wos-ai-capabilities">
              <h3 className="font-display font-bold text-lg text-trital-navy">What Rebeca can help with</h3>
              <ul className="mt-3 space-y-2 text-[13.5px] text-slate-600">
                {CAPABILITIES.map((x) => (
                  <li key={x} className="flex gap-2"><span className="text-trital-red">›</span>{x}</li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-trital-medical-blue-soft p-6">
              <h3 className="font-display font-bold text-lg text-trital-navy">Skip the chat</h3>
              <p className="text-[13px] text-slate-700 mt-1">Jump straight to action — every route works without AI.</p>
              <div className="mt-4 space-y-2">
                <Link to="/careers/jobs" className="flex items-center justify-between px-4 py-2.5 rounded-xl bg-white border border-slate-200 hover:border-trital-navy text-[13.5px] font-semibold text-trital-navy" data-testid="wos-ai-aside-jobs">
                  <span className="inline-flex items-center gap-2"><Search className="w-4 h-4" /> Search Healthcare Jobs</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <a href={SUBMIT_RESUME_URL} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between px-4 py-2.5 rounded-xl bg-white border border-slate-200 hover:border-trital-navy text-[13.5px] font-semibold text-trital-navy" data-testid="wos-ai-aside-resume">
                  <span className="inline-flex items-center gap-2"><FileUp className="w-4 h-4" /> Submit Resume</span>
                  <ExternalLink className="w-3.5 h-3.5 opacity-70" />
                </a>
                <a href={`sms:${RECRUITER_SMS_TEL}`} className="flex items-center justify-between px-4 py-2.5 rounded-xl bg-white border border-slate-200 hover:border-trital-navy text-[13.5px] font-semibold text-trital-navy" data-testid="wos-ai-aside-sms">
                  <span className="inline-flex items-center gap-2"><Phone className="w-4 h-4" /> Text Recruiter</span>
                  <span className="tabular-nums text-[12px] text-slate-500">{RECRUITER_PHONE_HUMAN}</span>
                </a>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* HOW IT WORKS -------------------------------------------------- */}
      <section className="py-12 bg-slate-50 border-y border-slate-200" data-testid="wos-ai-how">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8">
          <div className="overline text-trital-red">How the AI recruiter works</div>
          <h2 className="mt-2 font-display font-black text-3xl sm:text-4xl tracking-tight text-trital-navy leading-tight">
            One question at a time — never pushy, never inventing jobs.
          </h2>
          <p className="mt-3 max-w-[64ch] text-slate-600 leading-relaxed">
            Rebeca Miguel™ is built for healthcare staffing workflow. She asks the right questions, keeps your context throughout the chat, and guides you to the right next step on tritalcare.com.
          </p>
          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            {HOW_IT_WORKS.map(({ step, title, body, icon: Icon }) => (
              <div key={step} className="rounded-2xl bg-white border border-slate-200 p-6" data-testid={`wos-ai-how-${step}`}>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-[11px] font-bold text-trital-red tracking-widest">{step}</span>
                  <span className="w-9 h-9 rounded-xl bg-trital-medical-blue-soft text-trital-navy inline-flex items-center justify-center">
                    <Icon className="w-4.5 h-4.5" strokeWidth={1.8} />
                  </span>
                </div>
                <div className="mt-3 font-display font-bold text-[16.5px] text-trital-navy">{title}</div>
                <p className="mt-1.5 text-[13.5px] text-slate-600 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AUDIENCES ----------------------------------------------------- */}
      <section className="py-14" data-testid="wos-ai-audiences">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8">
          <div className="overline text-trital-red">Who Rebeca serves</div>
          <h2 className="mt-2 font-display font-black text-3xl sm:text-4xl tracking-tight text-trital-navy leading-tight">
            Built for nurses, allied health, and per diem clinicians.
          </h2>
          <div className="mt-8 grid lg:grid-cols-3 gap-5">
            {AUDIENCES.map(({ icon: Icon, eyebrow, title, bullets }) => (
              <div key={eyebrow} className="rounded-2xl bg-white border border-slate-200 p-6 hover:border-trital-navy/40 transition-colors" data-testid={`wos-ai-audience-${eyebrow.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="w-11 h-11 rounded-xl bg-trital-navy text-white inline-flex items-center justify-center">
                  <Icon className="w-5 h-5" strokeWidth={1.8} />
                </div>
                <div className="mt-4 overline text-trital-red">{eyebrow}</div>
                <div className="mt-1 font-display font-bold text-[17px] text-trital-navy leading-snug">{title}</div>
                <ul className="mt-3 space-y-1.5 text-[13.5px] text-slate-600">
                  {bullets.map((b) => (
                    <li key={b} className="flex gap-2"><span className="text-trital-red">›</span>{b}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPLIANCE NOTE ---------------------------------------------- */}
      <section className="pb-14" data-testid="wos-ai-compliance-note">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 lg:p-8 flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-700 inline-flex items-center justify-center shrink-0">
              <ShieldCheck className="w-5 h-5" strokeWidth={1.8} />
            </div>
            <div>
              <div className="overline text-emerald-700">Compliance &amp; Human Review</div>
              <p className="mt-2 text-[13.5px] text-slate-700 leading-relaxed">
                Rebeca Miguel™ supports candidate guidance and recruiting workflow. <b>Final job matching, submission, credential review, pay confirmation, and client approval require human recruiter review.</b> AI responses are informational and do not guarantee placement, hire, or pay package.
              </p>
            </div>
          </div>
        </div>
      </section>
    </WorkforceOsLayout>
  );
}
